import { FormEvent, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuthStore } from "@/stores/auth";
import { ApiError } from "@/api/client";
import { OAuthButtons } from "@/components/OAuthButtons";
import { AuthShell, AuthSubmit, PasswordField } from "@/components/AuthShell";

export function LoginPage() {
  const login = useAuthStore((s) => s.login);
  const loading = useAuthStore((s) => s.loading);
  const nav = useNavigate();
  const [params] = useSearchParams();
  const oauthError = params.get("oauth_error");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(oauthError ? `OAuth: ${oauthError}` : null);

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    try {
      const r = await login(email, password);
      if (r.requires2FA) {
        nav("/login/2fa", { replace: true });
        return;
      }
      nav("/projects", { replace: true });
    } catch (err) {
      if (err instanceof ApiError) {
        if (err.status === 423) setError("Account locked. Try again later.");
        else if (err.status === 401) setError("Invalid email or password.");
        else if (err.status === 403) setError("Account disabled.");
        else setError(err.message || "Login failed.");
      } else {
        setError("Network error.");
      }
    }
  };

  return (
    <AuthShell subtitle="Sign in to your workspace">
      <OAuthButtons />
      <form onSubmit={onSubmit} className="flex flex-col gap-3.5" noValidate>
        <label className="block">
          <span className="mb-1.5 block text-xs font-medium text-ef-textDim">Email</span>
          <input
            type="email"
            required
            autoFocus
            autoComplete="email"
            placeholder="you@example.com"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="ef-input"
          />
        </label>
        <PasswordField
          label="Password"
          autoComplete="current-password"
          placeholder="••••••••"
          value={password}
          onChange={setPassword}
        />
        {error && (
          <div className="rounded-[var(--radius-sm)] border border-ef-err/20 bg-ef-err/[0.08] px-3 py-2 text-xs text-ef-err">
            {error}
          </div>
        )}
        <AuthSubmit loading={loading}>{loading ? "Signing in…" : "Sign in"}</AuthSubmit>
      </form>
      {/* min-h-9 hit areas: these are standalone controls (no WCAG 2.5.8
          inline-text exemption) and were 15px tall on touch screens. */}
      <div className="mt-3 flex items-center justify-between text-xs text-ef-textMuted">
        <Link className="inline-flex min-h-9 items-center text-ef-accent hover:underline" to="/forgot-password">
          Forgot password?
        </Link>
        <span>
          No account?{" "}
          <Link className="inline-flex min-h-9 items-center text-ef-accent hover:underline" to="/register">
            Register
          </Link>
        </span>
      </div>
    </AuthShell>
  );
}
